import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  clearAllGISServices,
  toggleGISService,
} from '@rsos/reduxDataStore/src/features/gisServices/gisServicesSlice';
import checkEsriMapAccess from '@rsos/utils/checkEsriMapAccess';
import checkGISServicesAccess from '@rsos/utils/checkGISServicesAccess';
import usePortalType from '@rsos/utils/usePortalType';

const useGISHelpers = () => {
  const { isPremiumUser } = usePortalType();

  const hasEsriMapAccess =
    useSelector(state =>
      checkEsriMapAccess({
        psaps: state.psaps,
        sinatra: state.sinatra,
      }),
    ) && isPremiumUser;

  const hasGISServicesAccess = useSelector(state =>
    checkGISServicesAccess({
      psaps: state.psaps,
      sinatra: state.sinatra,
    }),
  );

  const gisSupplementalServices = useSelector(
    state => state.gisServices.supplementalServices,
  );
  const supplementalServicesSublayers = useSelector(
    state => state.gisServices.supplementalServicesSublayers,
  );
  const basemapServiceSublayers = useSelector(
    state => state.gisServices.basemapServiceSublayers,
  );
  const basemapService = useSelector(state => state.gisServices.basemapService);

  const gisWMSServices = useSelector(state => state.gisServices.wmsServices);

  const gisWMTSServices = useSelector(state => state.gisServices.wmtsServices);

  const gisMapLayersControlServices = useMemo(() => {
    let processedSupplementalServices = [...gisSupplementalServices];

    if (supplementalServicesSublayers.length) {
      const serviceSublayersMap = new Map();
      let currentSublayers = [];
      let currentServiceId = null;

      supplementalServicesSublayers.forEach(sublayer => {
        if (currentServiceId === null) {
          currentServiceId = sublayer.id;
          currentSublayers = [sublayer];
        } else if (sublayer.id === currentServiceId) {
          currentSublayers.push(sublayer);
        } else {
          serviceSublayersMap.set(currentServiceId, currentSublayers);
          currentServiceId = sublayer.id;
          currentSublayers = [sublayer];
        }
      });

      if (currentServiceId !== null) {
        serviceSublayersMap.set(currentServiceId, currentSublayers);
      }

      processedSupplementalServices = gisSupplementalServices.map(service => {
        const sublayers = serviceSublayersMap.get(service.id);
        if (sublayers) {
          return {
            ...service,
            sublayers: sublayers,
          };
        }
        return service;
      });
    }

    // Process basemap service with sublayers
    const processedBasemapServices = [];

    if (basemapServiceSublayers.length && Object.keys(basemapService).length) {
      processedBasemapServices.push({
        ...basemapService,
        sublayers: basemapServiceSublayers,
      });
    }

    return [
      ...processedBasemapServices,
      ...processedSupplementalServices,
      ...gisWMSServices,
      ...gisWMTSServices,
    ];
  }, [
    gisSupplementalServices,
    gisWMSServices,
    gisWMTSServices,
    supplementalServicesSublayers,
    basemapServiceSublayers,
    basemapService,
  ]);

  const hasGISMapLayersControlServices = useMemo(() => {
    return !!(
      hasEsriMapAccess &&
      hasGISServicesAccess &&
      gisMapLayersControlServices?.length
    );
  }, [hasEsriMapAccess, hasGISServicesAccess, gisMapLayersControlServices]);

  const activeGISServices = useSelector(
    state => state.gisServices.activeGISServices,
  );

  const numActiveGISServices = activeGISServices.length;

  return {
    activeGISServices,
    clearAllGISServices,
    gisMapLayersControlServices,
    hasEsriMapAccess,
    hasGISMapLayersControlServices,
    numActiveGISServices,
    toggleGISService,
  };
};

export default useGISHelpers;
